/** @format */
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { _$, Row, Col, Table, TableColumn, TableSortValue, Pagination } from '@king-design/react'
import {
  getAgentKitOverviewMetrics,
  getAgentKitOverview,
  getAgentKitServiceList,
  AgentKitServiceOrderBy
} from 'src/api/ApiAgentKit'
import { useAsync } from 'react-use'
import { PageProps, initPage } from 'src/utils/interface'
import { AgentKitOverviewProps } from '../interface'
import { TableSearch, parseTableSearchParams, MonitorSearch } from 'src/components'
import { numberFormat } from 'src/utils/common'
import variable from 'src/static/styles/var'
import OverviewLineChart, {
  OverviewLineChartPoint
} from 'src/pages/AIObservability/components/OverviewLineChart'
import { fillMetricPoints } from 'src/pages/AIObservability/ServiceOverview/container/constant'
import { useHistory, useLocation } from 'react-router'
import {
  MetricsSection,
  MetricCard,
  ChartsSection,
  ValueWithUnit,
  splitTokenValue
} from '../components'

interface SearchProps extends PageProps {
  Keyword: string
}

const AgentKitOverview: React.FC<AgentKitOverviewProps> = ({
  className,
  quickKey,
  serviceName,
  startTime,
  endTime,
  region,
  step
}) => {
  const history = useHistory()
  const location = useLocation()
  const [search, setSearch] = useState<SearchProps>(
    () =>
      parseTableSearchParams(location.search) || {
        ...initPage,
        Keyword: ''
      }
  )
  const [tableSort, setTableSort] = useState<TableSortValue>()

  const { value: metricData, loading: metricLoading } = useAsync(async () => {
    if (!region || !startTime || !endTime) {
      return
    }
    const result = await getAgentKitOverview({
      StartTime: startTime,
      EndTime: endTime,
      ServiceName: serviceName
    })
    return result
  }, [region, startTime, endTime, serviceName])

  const tokenUsage = useMemo(
    () => splitTokenValue(metricData?.TokenTotalUsage),
    [metricData?.TokenTotalUsage]
  )

  const errorCount = Number(metricData?.ErrorCount)
  const hasErrorCount = !Number.isNaN(errorCount) && errorCount > 0

  const { value: chartData, loading: chartLoading } = useAsync(async () => {
    if (!region || !startTime || !endTime || !step) {
      return
    }
    const result = await getAgentKitOverviewMetrics({
      StartTime: startTime,
      EndTime: endTime,
      Step: `${step}s`,
      ServiceName: serviceName
    })
    return (result?.Series ?? []).reduce((acc, item) => {
      acc[item.Metric] = item.Points?.length
        ? fillMetricPoints(item.Points, startTime, endTime, step)
        : []
      return acc
    }, {} as Record<string, OverviewLineChartPoint[]>)
  }, [region, startTime, endTime, step, serviceName])

  const errorRate: OverviewLineChartPoint[] = useMemo(() => {
    return (
      chartData?.ErrorRate?.map(item => {
        return {
          ...item,
          Value: Number(numberFormat(item.Value * 100))
        }
      }) || []
    )
  }, [chartData?.ErrorRate])

  const { value: runtimeData, loading: runtimeLoading } = useAsync(async () => {
    if (serviceName || !region || !startTime || !endTime) {
      return
    }
    const result = await getAgentKitServiceList({
      StartTime: startTime,
      EndTime: endTime,
      Keyword: search.Keyword,
      OrderBy: tableSort?.key as AgentKitServiceOrderBy | undefined,
      Order: tableSort?.type,
      Page: search.Page,
      PageSize: search.PageSize
    })
    return result
  }, [serviceName, region, startTime, endTime, search, tableSort])

  const loading = metricLoading || chartLoading || runtimeLoading

  return (
    <div className={className}>
      <MetricsSection loading={loading} aria-label={_$('指标')}>
        {!serviceName && metricData?.RuntimeCount !== undefined ? (
          <MetricCard label={_$('服务数')} value={metricData?.RuntimeCount ?? '-'} />
        ) : null}
        <MetricCard label={_$('会话数')} value={metricData?.SessionCount ?? '-'} />
        <MetricCard
          label={_$('Token消耗数')}
          value={<ValueWithUnit value={tokenUsage.value} unit={tokenUsage.unit} />}
        />
        <MetricCard label={_$('调用数')} value={metricData?.InvocationCount ?? '-'} />
        <MetricCard
          label={_$('错误数')}
          value={metricData?.ErrorCount ?? '-'}
          hasError={hasErrorCount}
        />
        <MetricCard
          label={_$('错误率')}
          hasError={hasErrorCount}
          value={
            <ValueWithUnit
              value={
                metricData?.ErrorRate !== null ? numberFormat(metricData?.ErrorRate * 100) : '-'
              }
              unit="%"
            />
          }
        />
      </MetricsSection>

      <ChartsSection aria-label={_$('趋势图')}>
        <Row gutter="16">
          <Col span={8} className="overview-chart-col">
            <OverviewLineChart
              title={_$('会话数')}
              data={chartData?.SessionCount}
              colHeight={300}
            />
          </Col>
          {serviceName ? (
            <Col span={8} className="overview-chart-col">
              <OverviewLineChart
                title={_$('Token消耗数')}
                data={chartData?.TokenTotalUsage}
                tokenValue
                colHeight={300}
              />
            </Col>
          ) : null}
          <Col span={8} className="overview-chart-col">
            <OverviewLineChart
              title={_$('调用数')}
              data={chartData?.InvocationCount}
              colHeight={300}
            />
          </Col>
          {serviceName ? (
            <Col span={12} className="overview-chart-col">
              <OverviewLineChart
                title={_$('错误数')}
                lineColor={variable.errorColor}
                data={chartData?.ErrorCount}
                colHeight={300}
              />
            </Col>
          ) : null}
          <Col span={serviceName ? 12 : 8} className="overview-chart-col">
            <OverviewLineChart
              title={_$('错误率')}
              data={errorRate}
              lineColor={variable.errorColor}
              yPercent
              colHeight={300}
            />
          </Col>
        </Row>
      </ChartsSection>

      {!serviceName ? (
        <section className="overview-table" aria-label={_$('服务列表')}>
          <div className="service-list-header">
            <h2 className="service-list-header-title">{_$('Runtime列表')}</h2>
            <TableSearch params={search}>
              <MonitorSearch
                placeholder={_$('请输入服务名称，支持模糊搜索')}
                value={search.Keyword}
                onChange={value => {
                  setSearch({
                    ...search,
                    Page: 1,
                    Keyword: value
                  })
                }}
              />
            </TableSearch>
          </div>
          <Table
            data={runtimeData?.list || []}
            rowKey={data => data.Id}
            checkType="none"
            animation={false}
            loading={runtimeLoading}
            sort={tableSort}
            onChangeSort={sort => {
              setTableSort(sort)
            }}
          >
            <TableColumn
              key="RuntimeName"
              title={_$('Runtime名称')}
              sortable
              slotTemplate={([data]) => {
                return (
                  <a
                    onClick={() => {
                      const quick = quickKey && quickKey !== 'custom'
                      let url = `/monitor/agentkit/runtimedetail?serviceName=${encodeURIComponent(
                        data.ServiceName ?? ''
                      )}&runtime=${encodeURIComponent(data.RuntimeName ?? '')}`
                      if (quick) {
                        url += `&quickKey=${quickKey}`
                      } else {
                        url += `&startTime=${startTime}&endTime=${endTime}`
                      }
                      history.push(url)
                    }}
                  >
                    {data.RuntimeName}
                  </a>
                )
              }}
            />
            <TableColumn
              key="Models"
              title={_$('模型')}
              slotTemplate={([data]) => {
                return <span>{data.Models.join('、')}</span>
              }}
            />
            <TableColumn key="SessionCount" sortable title={_$('会话数')} />
            <TableColumn key="InvocationCount" sortable title={_$('调用数')} />
            <TableColumn
              key="ErrorRate"
              sortable
              title={_$('错误率')}
              slotTemplate={([data]) => {
                return (
                  <span style={data.ErrorRate > 0 ? { color: variable.errorColor } : null}>
                    {numberFormat(data.ErrorRate * 100)}%
                  </span>
                )
              }}
            />
          </Table>
          {runtimeData !== undefined ? (
            <Pagination
              showGoto
              value={search.Page}
              limit={search.PageSize}
              total={runtimeData?.total || 0}
              size="small"
              onChangeValue={value => {
                setSearch(state => {
                  return {
                    ...state,
                    Page: value
                  }
                })
              }}
              onChangeLimit={limit => {
                setSearch({
                  ...search,
                  PageSize: limit
                })
              }}
            />
          ) : null}
        </section>
      ) : null}
    </div>
  )
}

export default styled(AgentKitOverview)`
  .overview-table {
    .service-list-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin: 16px 0;
      .service-list-header-title {
        font-size: 14px;
        font-weight: 600;
        line-height: 22px;
        color: rgba(0, 0, 0, 0.85);
      }
    }
  }
`
